import {lazy} from 'react'
import {Navigate} from 'react-router-dom'
import RequireSubscription from '@components/routing/RequireSubscription.jsx'
import {getAuthData} from '@utils/auth.js'
import {flattenNavigation, navigationConfig} from '@shared/navigationConfig'

const LoginForm = lazy(() => import('@components/LoginForm.jsx'))
const CreateNewAccount = lazy(() => import('@components/CreateNewAccount.jsx'))
const ForgotPassword = lazy(() => import('@components/ForgotPassword.jsx'))
const ResetPassword = lazy(() => import('@components/ResetPassword.jsx'))
const AppShell = lazy(() => import('@layouts/AppShell.jsx'))
const VerifyEmail = lazy(() => import('@pages/VerifyEmail/VerifyEmail.jsx'))
const DashboardIncome = lazy(() => import('@pages/DashboardIncome/DashboardIncome.jsx'))
const DashboardTransactions = lazy(() => import('@pages/Dashboard2/Dashboard2.jsx'))
const TransactionsHub = lazy(() => import('@pages/TransactionsHub/TransactionsHub.jsx'))
const FinanceAnalysis = lazy(() => import('@pages/FinanceAnalysis/FinanceAnalysis.jsx'))
const AccountManager = lazy(() => import('@pages/AccountManager/AccountManager.jsx'))
const DebtManager = lazy(() => import('@pages/DebtManager/DebtManager.jsx'))
const ObligationsHub = lazy(() => import('@pages/ObligationsHub/ObligationsHub.jsx'))
const RecurrentExpenseManager = lazy(() => import('@pages/RecurrentExpenseManager/RecurrentExpenseManager.jsx'))
const RecurrentIncomeManager = lazy(() => import('@pages/RecurrentIncomeManager/RecurrentIncomeManager.jsx'))
const RecurrentHub = lazy(() => import('@pages/RecurrentHub/RecurrentHub.jsx'))
const BudgetManager = lazy(() => import('@pages/BudgetManager/BudgetManager.jsx'))
const ExpenseGroupManager = lazy(() => import('@pages/ExpenseGroupManager/ExpenseGroupManager.jsx'))
const Forecast = lazy(() => import('@pages/Forecast/Forecast.jsx'))
const ForecastSummaryCalendar = lazy(() => import('@pages/ForecastSummaryCalendar/ForecastSummaryCalendar.jsx'))
const BalanceHistory = lazy(() => import('@pages/BalanceHistory/BalanceHistory.jsx'))
const SetupProfile = lazy(() => import('@pages/SetupProfile/SetupProfile.jsx'))
const GptAnalysis = lazy(() => import('@pages/GptAnalysis/GptAnalysis.jsx'))
const GptAnalysisDetail = lazy(() => import('@pages/GptAnalysis/GptAnalysisDetail.jsx'))
const FinanceAnalysisHistory = lazy(() => import('@pages/FinanceAnalysisHistory/FinanceAnalysisHistory.jsx'))
const FinanceAnalysisHistoryDetail = lazy(() => import('@pages/FinanceAnalysisHistory/FinanceAnalysisHistoryDetail.jsx'))
const ForecastAiHistory = lazy(() => import('@pages/ForecastAiHistory/ForecastAiHistory.jsx'))
const ForecastAiHistoryDetail = lazy(() => import('@pages/ForecastAiHistory/ForecastAiHistoryDetail.jsx'))
const SettingsActivityLog = lazy(() => import('@pages/SettingsActivityLog/SettingsActivityLog.jsx'))
const RewardManager = lazy(() => import('@pages/RewardManager/RewardManager.jsx'))
const DebtPayoffPlan = lazy(() => import('@pages/DebtPayoffPlan/DebtPayoffPlan.jsx'))
const CategoryManager = lazy(() => import('@pages/CategoryManager/CategoryManager.jsx'))
const ClosingHistory = lazy(() => import('@pages/ClosingHistory/ClosingHistory.jsx'))
const GoalManager = lazy(() => import('@pages/GoalManager/GoalManager.jsx'))
const GrowthProgram = lazy(() => import('@pages/GrowthProgram/GrowthProgram.jsx'))
const SkillsFactory = lazy(() => import('@pages/SkillsFactory/SkillsFactory.jsx'))
const PersonalCircle = lazy(() => import('@pages/PersonalCircle/PersonalCircle.jsx'))
const InviteFriend = lazy(() => import('@pages/InviteFriend/InviteFriend.jsx'))
const Subscribe = lazy(() => import('@pages/Subscribe/Subscribe.jsx'))
const BillingHistory = lazy(() => import('@pages/BillingHistory/BillingHistory.jsx'))
const Passkeys = lazy(() => import('@pages/Passkeys/Passkeys.jsx'))
const SubscribeFriends = lazy(() => import('@pages/SubscribeFriends/SubscribeFriends.jsx'))
const AccountDeletion = lazy(() => import('@pages/AccountDeletion/AccountDeletion.jsx'))
const AccountDeletionConfirm = lazy(() => import('@pages/AccountDeletion/AccountDeletionConfirm.jsx'))
const Home = lazy(() => import('@pages/Home/Home.jsx'))
const Notifications = lazy(() => import('@pages/Notifications/Notifications.jsx'))
const TasksToConfirm = lazy(() => import('@pages/TasksToConfirm/TasksToConfirm.jsx'))
const TaskEvents = lazy(() => import('@pages/TaskEvents/TaskEvents.jsx'))
const BalanceRollout = lazy(() => import('@pages/BalanceRollout/BalanceRollout.jsx'))
const ForecastRollout = lazy(() => import('@pages/ForecastRollout/ForecastRollout.jsx'))
const TempoMint = lazy(() => import('@pages/TempoMint/TempoMint.jsx'))
const DeadlineManager = lazy(() => import('@pages/DeadlineManager/DeadlineManager.jsx'))
const MyInsights = lazy(() => import('@pages/MyInsights/MyInsights.jsx'))
const MyInsightDetail = lazy(() => import('@pages/MyInsights/MyInsightDetail.jsx'))
const PublicInsights = lazy(() => import('@pages/PublicInsights/PublicInsights.jsx'))
const PublicInsightDetail = lazy(() => import('@pages/PublicInsights/PublicInsightDetail.jsx'))
const LegalTerms = lazy(() => import('@pages/Legal/Terms.jsx'))
const LegalPrivacy = lazy(() => import('@pages/Legal/Privacy.jsx'))
const LegalRefund = lazy(() => import('@pages/Legal/Refund.jsx'))
const LegalAbout = lazy(() => import('@pages/Legal/About.jsx'))
const SupportHelp = lazy(() => import('@pages/SupportHelp/SupportHelp.jsx'))
const SupportHelpPublic = lazy(() => import('@pages/SupportHelp/SupportHelpPublic.jsx'))
const PushTest = lazy(() => import('@pages/PushTest/PushTest.jsx'))

const country = getAuthData()?.user?.settings?.country

const routeFactories = new Map([
  ['/', () => ({index: true, element: <Home/>})],
  ['/notifications', () => ({path: 'notifications', element: <Notifications/>})],
  ['/tasks-to-confirm', () => ({path: 'tasks-to-confirm', element: <TasksToConfirm/>})],
  ['/task-events', () => ({path: 'task-events', element: <TaskEvents/>})],
  ['/tempo-mint', () => ({path: 'tempo-mint', element: <TempoMint/>})],
  ['/deadline-list', () => ({path: 'deadline-list', element: <DeadlineManager/>})],
  ['/transactions-hub', () => ({path: 'transactions-hub', element: <TransactionsHub/>})],
  ['/finance-analysis', () => ({path: 'finance-analysis', element: <FinanceAnalysis/>})],
  ['/expenses-list', () => ({path: 'expenses-list', element: <DashboardTransactions/>})],
  ['/income-list', () => ({path: 'income-list', element: <DashboardIncome/>})],
  ['/account-list', () => ({path: 'account-list', element: <AccountManager/>})],
  ['/obligations', () => ({path: 'obligations', element: <ObligationsHub/>})],
  ['/debt-list', () => ({path: 'debt-list', element: <DebtManager/>})],
  ['/debt-payoff', () => ({path: 'debt-payoff', element: <DebtPayoffPlan/>})],
  ['/budget-list', () => ({path: 'budget-list', element: <BudgetManager/>})],
  ['/expense-group-list', () => ({path: 'expense-group-list', element: <ExpenseGroupManager/>})],
  ['/recurrent-expense-list', () => ({path: 'recurrent-expense-list', element: <RecurrentExpenseManager/>})],
  ['/recurrent-income-list', () => ({path: 'recurrent-income-list', element: <RecurrentIncomeManager/>})],
  ['/recurrent-hub', () => ({path: 'recurrent-hub', element: <RecurrentHub/>})],
  ['/forecast', () => ({path: 'forecast', element: <Forecast/>})],
  ['/forecast-summary', () => ({
    path: 'forecast-summary',
    element: <Navigate to="/forecast-summary-calendar" replace/>,
  })],
  ['/forecast-summary-calendar', () => ({path: 'forecast-summary-calendar', element: <ForecastSummaryCalendar/>})],
  ['/gpt-analysis', () => ({path: 'gpt-analysis', element: <GptAnalysis/>})],
  ['/gpt-analysis/:id', () => ({path: 'gpt-analysis/:id', element: <GptAnalysisDetail/>})],
  ['/settings/finance-analysis-history', () => ({path: 'settings/finance-analysis-history', element: <FinanceAnalysisHistory/>})],
  ['/settings/finance-analysis-history/:id', () => ({path: 'settings/finance-analysis-history/:id', element: <FinanceAnalysisHistoryDetail/>})],
  ['/settings/forecast-ai-history', () => ({path: 'settings/forecast-ai-history', element: <ForecastAiHistory/>})],
  ['/settings/forecast-ai-history/:id', () => ({path: 'settings/forecast-ai-history/:id', element: <ForecastAiHistoryDetail/>})],
  ['/settings/activity-log', () => ({path: 'settings/activity-log', element: <SettingsActivityLog/>})],
  ['/settings/balance-rollout', () => ({path: 'settings/balance-rollout', element: <BalanceRollout/>})],
  ['/settings/forecast-rollout', () => ({path: 'settings/forecast-rollout', element: <ForecastRollout/>})],
  ['/settings/support-help', () => ({path: 'settings/support-help', element: <SupportHelp/>})],
  ['/balance-history', () => ({path: 'balance-history', element: <BalanceHistory/>})],
  ['/rewards', () => ({path: 'rewards', element: <RewardManager/>})],
  ['/finance-goals', () => ({path: 'finance-goals', element: <GoalManager/>})],
  ['/categories', () => ({path: 'categories', element: <CategoryManager/>})],
  ['/closing-history', () => ({path: 'closing-history', element: <ClosingHistory/>})],
  ['/growth-program', () => ({path: 'growth-program', element: <GrowthProgram/>})],
  ['/skills-factory', () => ({path: 'skills-factory', element: <SkillsFactory/>})],
  ['/personal-circle', () => ({path: 'personal-circle', element: <PersonalCircle/>})],
  ['/invite-friend', () => ({path: 'invite-friend', element: <InviteFriend/>})],
  ['/settings/delete-account', () => ({path: 'settings/delete-account', element: <AccountDeletion/>})],
  ['/my-insights', () => ({path: 'my-insights', element: <MyInsights/>})],
  ['/my-insights/:insightId', () => ({path: 'my-insights/:insightId', element: <MyInsightDetail/>})],
  ['/public-insights', () => ({path: 'public-insights', element: <PublicInsights/>})],
  ['/public-insights/:insightId', () => ({path: 'public-insights/:insightId', element: <PublicInsightDetail/>})],
  ['/setup-profile', () => ({index: true, element: <SetupProfile/>})],
  ['/subscribe', () => ({index: true, element: <Subscribe country={country}/>})],
  ['/settings/billing-history', () => ({path: 'settings/billing-history', element: <BillingHistory/>})],
  ['/settings/passkeys', () => ({path: 'settings/passkeys', element: <Passkeys/>})],
])

const navigationRouteMeta = flattenNavigation(navigationConfig).filter(node => typeof node.path === 'string' && node.path)

const appShellChildRoutes = []
const standaloneShellRoutes = []

for (const meta of navigationRouteMeta) {
  const factory = routeFactories.get(meta.path)
  if (!factory) continue

  const definition = factory()
  if (!definition) continue

  const finalRoute = {...definition}

  if (!finalRoute.index) {
    finalRoute.path = finalRoute.path || meta.path.replace(/^\//, '')
  }

  const allowedPlans = Array.isArray(meta.allowedPlans)
    ? meta.allowedPlans.map(plan => plan.toLowerCase())
    : []
  const requiresFullPlan = allowedPlans.includes('full') && !allowedPlans.some(plan => plan === 'basic' || plan === 'free')

  if (requiresFullPlan && meta.layout !== 'standalone') {
    finalRoute.element = (
      <RequireSubscription plan="full" country={country}>
        {finalRoute.element}
      </RequireSubscription>
    )
  }

  if (meta.layout === 'standalone') {
    const normalizedMetaPath = meta.path.replace(/^\//, '')
    const childMatchesParentPath = finalRoute.path === meta.path || finalRoute.path === normalizedMetaPath

    if (!finalRoute.index && childMatchesParentPath) {
      delete finalRoute.path
      finalRoute.index = true
    }

    standaloneShellRoutes.push({
      path: meta.path,
      element: <AppShell/>,
      protected: true,
      children: [finalRoute],
    })
  } else {
    appShellChildRoutes.push(finalRoute)
  }
}

appShellChildRoutes.push({
  path: 'forecast-summary',
  element: <Navigate to="/forecast-summary-calendar" replace/>,
})

appShellChildRoutes.push({
  path: 'liability-list',
  element: <Navigate to="/obligations" replace/>,
})

export const routes = [
  {
    path: '/login',
    element: <LoginForm/>,
    protected: false,
  },
  {
    path: '/register',
    element: <CreateNewAccount/>,
    protected: false,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword/>,
    protected: false,
  },
  {
    path: '/reset-password',
    element: <ResetPassword/>,
    protected: false,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail/>,
    protected: false,
  },
  {
    path: '/terms',
    element: <LegalTerms/>,
    protected: false,
  },
  {
    path: '/about',
    element: <LegalAbout/>,
    protected: false,
  },
  {
    path: '/privacy',
    element: <LegalPrivacy/>,
    protected: false,
  },
  {
    path: '/refund',
    element: <LegalRefund/>,
    protected: false,
  },
  {
    path: '/support-help',
    element: <SupportHelpPublic/>,
    protected: false,
  },
  {
    path: '/push-test',
    element: <PushTest/>,
    protected: false,
  },
  {
    path: '/account-deletion/confirm',
    element: <AccountDeletionConfirm/>,
    protected: false,
  },
  {
    path: '/subscribe/friends',
    element: <AppShell/>,
    protected: true,
    children: [{index: true, element: <SubscribeFriends country={country}/> }]
  },
  ...standaloneShellRoutes,
  {
    path: '/',
    element: (
      <RequireSubscription plan="basic" country={country}>
        <AppShell/>
      </RequireSubscription>
    ), // ⛱ защищённый layout
    protected: true,
    children: appShellChildRoutes,
  },
]
